import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { LocalizationService, ToastService } from '@services';
import { LanguageCodesEnum } from '@constants';
import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { Line } from '../common/Line';
import { Separator } from '../common/Separator';
import { Touchable } from '../common/Touchable';
import { Popover } from './Popover';
import { useAuthModel } from '@models/AuthModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { parseApiError } from '@utils';
import { usePopover } from '@hooks';

const Container = styled(View)({
  flex: 1,
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  width: 115,
});

const Text = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
  px: '$4',
  flexGrow: 1,
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: '$4',
});

const Label = styled(BaseText)({
  variant: 'text.medium',
});

const MenuContainer = styled(View)({
  width: 132,
  bg: '$background0',
  px: '$4',
  py: '$2',
});

interface IActiveContainerProps {
  active: boolean;
}

const ActiveContainer = styled(View)(
  (props: IActiveContainerProps) => ({
    flexGrow: 1,
    pl: '$4',
    py: '$2',
    bg: props.active ? '$primary80' : null,
    borderRadius: 8,
  }),
);

export const LanguageMenu = observer(() => {
  const { t } = useTranslation();
  const { ref, togglePopover } = usePopover();
  const authModel = useAuthModel();
  const viewer = useViewerModel();
  const currentLangCode = LocalizationService.language;

  async function changeLanguage(code: LanguageCodesEnum) {
    LocalizationService.changeLanguage(code);
    togglePopover();

    if (!authModel.isAuthorized) {
      return;
    }

    try {
      await viewer.update.run({
        settings: {
          language: code,
        },
      });
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  return (
    <Container>
      <Popover ref={ref}>
        <Touchable onPress={togglePopover}>
          <Separator size="$5" />
          <RowContainer>
            <Icon name="world-bold" color="$text400" size={20} />
            <Text>{t(`common.languages.${currentLangCode}`)}</Text>
            <Icon
              name="small-arrow-down-light"
              color="$text400"
              size={20}
            />
          </RowContainer>
          <Separator size="$5" />
        </Touchable>

        <MenuContainer>
          {Object.values(LanguageCodesEnum).map((code, index) => {
            const active = currentLangCode === code;

            return (
              <Fragment key={index}>
                {index !== 0 && <Line />}

                <TouchableContainer
                  onPress={() => changeLanguage(code)}
                >
                  <ActiveContainer active={active}>
                    <Label>{t(`common.languages.${code}`)}</Label>
                  </ActiveContainer>
                </TouchableContainer>
              </Fragment>
            );
          })}
        </MenuContainer>
      </Popover>
    </Container>
  );
});
